package com.zq.webproject.ui.purchaserecord

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Link
import androidx.compose.material3.*
import androidx.compose.material3.TabRowDefaults.tabIndicatorOffset
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import coil3.compose.AsyncImage
import com.zq.webproject.model.PurchaseRecord
import com.zq.webproject.utils.UrlLauncher
import org.jetbrains.compose.resources.Font
import webproject.composeapp.generated.resources.Res
import webproject.composeapp.generated.resources.font_song

// 统一字体（使用宋体，包含完整中文字符）
private val CaiFont: FontFamily
    @Composable
    get() = FontFamily(Font(Res.font.font_song))

/**
 * 购买记录页面
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PurchaseRecordScreen(
    onBackClick: () -> Unit,
    onOrderClick: (String) -> Unit = {}  // 新增订单详情回调
) {
    println("PurchaseRecordScreen - 渲染购买记录页面") // 日志输出

    // 创建ViewModel实例
    val viewModel = remember { PurchaseRecordViewModel() }

    // Tab状态：0-待付款，1-已付款
    var selectedTabIndex by remember { mutableStateOf(0) }

    // 页面加载时获取购买记录
    LaunchedEffect(Unit) {
        println("PurchaseRecordScreen - 开始加载购买记录数据") // 日志输出
        viewModel.loadPurchaseRecords()
    }

    // 根据Tab筛选数据
    val filteredRecords = remember(viewModel.purchaseRecords, selectedTabIndex) {
        println("PurchaseRecordScreen - 筛选数据，当前Tab: $selectedTabIndex") // 日志输出
        when (selectedTabIndex) {
            0 -> viewModel.purchaseRecords.filter { it.payStatus == 0 } // 待付款
            1 -> viewModel.purchaseRecords.filter { it.payStatus == 1 } // 已付款
            else -> viewModel.purchaseRecords
        }
    }

    Scaffold(
        topBar = {
            TopAppBar(
                title = {
                    Text(
                        text = "购买记录",
                        fontFamily = CaiFont,
                        fontWeight = FontWeight.Bold
                    )
                },
                navigationIcon = {
                    IconButton(onClick = {
                        println("PurchaseRecordScreen - 点击返回按钮") // 日志输出
                        onBackClick()
                    }) {
                        Icon(
                            imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                            contentDescription = "返回"
                        )
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = Color(0xFF1E88E5),
                    titleContentColor = Color.White,
                    navigationIconContentColor = Color.White
                )
            )
        }
    ) { paddingValues ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(paddingValues)
                .background(Color(0xFFF5F5F5))
        ) {
            // Tab切换栏
            TabRow(
                selectedTabIndex = selectedTabIndex,
                containerColor = Color.White,
                contentColor = Color(0xFF1E88E5),
                indicator = { tabPositions ->
                    TabRowDefaults.SecondaryIndicator(
                        Modifier.tabIndicatorOffset(tabPositions[selectedTabIndex]),
                        color = Color(0xFF1E88E5)
                    )
                }
            ) {
                Tab(
                    selected = selectedTabIndex == 0,
                    onClick = {
                        println("PurchaseRecordScreen - 切换到待付款Tab") // 日志输出
                        selectedTabIndex = 0
                    },
                    text = {
                        Text(
                            text = "待付款",
                            fontFamily = CaiFont,
                            fontWeight = if (selectedTabIndex == 0) FontWeight.Bold else FontWeight.Normal
                        )
                    }
                )
                Tab(
                    selected = selectedTabIndex == 1,
                    onClick = {
                        println("PurchaseRecordScreen - 切换到已付款Tab") // 日志输出
                        selectedTabIndex = 1
                    },
                    text = {
                        Text(
                            text = "已付款",
                            fontFamily = CaiFont,
                            fontWeight = if (selectedTabIndex == 1) FontWeight.Bold else FontWeight.Normal
                        )
                    }
                )
            }

            // 内容区域
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(Color(0xFFF5F5F5))
            ) {
                when {
                    // 加载中
                    viewModel.isLoading -> {
                        Box(
                            modifier = Modifier.fillMaxSize(),
                            contentAlignment = Alignment.Center
                        ) {
                            Column(
                                horizontalAlignment = Alignment.CenterHorizontally
                            ) {
                                CircularProgressIndicator(
                                    color = Color(0xFF1E88E5)
                                )
                                Spacer(modifier = Modifier.height(16.dp))
                                Text(
                                    text = "加载中...",
                                    fontSize = 14.sp,
                                    color = Color.Gray,
                                    fontFamily = CaiFont
                                )
                            }
                        }
                    }

                    // 加载失败
                    viewModel.errorMessage != null -> {
                        Box(
                            modifier = Modifier.fillMaxSize(),
                            contentAlignment = Alignment.Center
                        ) {
                            Column(
                                horizontalAlignment = Alignment.CenterHorizontally,
                                modifier = Modifier.padding(32.dp)
                            ) {
                                Text(
                                    text = viewModel.errorMessage ?: "加载失败",
                                    fontSize = 14.sp,
                                    color = Color.Red,
                                    fontFamily = CaiFont
                                )
                                Spacer(modifier = Modifier.height(16.dp))
                                Button(
                                    onClick = {
                                        println("PurchaseRecordScreen - 点击重试按钮") // 日志输出
                                        viewModel.loadPurchaseRecords()
                                    },
                                    colors = ButtonDefaults.buttonColors(
                                        containerColor = Color(0xFF1E88E5)
                                    )
                                ) {
                                    Text(
                                        text = "重试",
                                        fontFamily = CaiFont
                                    )
                                }
                            }
                        }
                    }

                    // 无数据
                    filteredRecords.isEmpty() && !viewModel.isLoading && viewModel.errorMessage == null -> {
                        Box(
                            modifier = Modifier.fillMaxSize(),
                            contentAlignment = Alignment.Center
                        ) {
                            Text(
                                text = if (selectedTabIndex == 0) "暂无待付款记录" else "暂无已付款记录",
                                fontSize = 14.sp,
                                color = Color.Gray,
                                fontFamily = CaiFont
                            )
                        }
                    }

                    // 显示列表
                    else -> {
                        LazyColumn(
                            modifier = Modifier.fillMaxSize(),
                            contentPadding = PaddingValues(16.dp),
                            verticalArrangement = Arrangement.spacedBy(12.dp)
                        ) {
                            items(filteredRecords) { record ->
                                PurchaseRecordItem(
                                    record = record,
                                    onClick = {
                                        println("PurchaseRecordScreen - 点击订单记录，订单号: ${record.orderNo}") // 日志输出
                                        onOrderClick(record.orderNo)
                                    }
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}
/**
 * 购买记录项
 */
@Composable
fun PurchaseRecordItem(
    record: PurchaseRecord,
    onClick: () -> Unit = {}
) {
    Card(
        modifier = Modifier
            .fillMaxWidth()
            .clickable {
                println("PurchaseRecordItem - 点击订单卡片，订单号: ${record.orderNo}") // 日志输出
                onClick()
            },
        shape = RoundedCornerShape(12.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(2.dp)
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.dp)
        ) {
            // 订单号和支付状态
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = "订单号: ${record.orderNo}",
                    fontSize = 12.sp,
                    color = Color.Gray,
                    fontFamily = CaiFont
                )

                // 支付状态标签
                Surface(
                    shape = RoundedCornerShape(4.dp),
                    color = if (record.payStatus == 1) Color(0xFF4CAF50) else Color(0xFFFF9800)
                ) {
                    Text(
                        text = record.getPayStatusText(),
                        fontSize = 11.sp,
                        color = Color.White,
                        fontFamily = CaiFont,
                        modifier = Modifier.padding(horizontal = 8.dp, vertical = 4.dp)
                    )
                }
            }

            Spacer(modifier = Modifier.height(12.dp))

            // 商品信息
            Row(
                modifier = Modifier.fillMaxWidth()
            ) {
                // 商品图片
                AsyncImage(
                    model = record.productImage.replace("http://", "https://"),
                    contentDescription = "商品图片",
                    modifier = Modifier
                        .size(80.dp)
                        .background(Color(0xFFF5F5F5), RoundedCornerShape(8.dp)),
                    contentScale = ContentScale.Crop
                )

                Spacer(modifier = Modifier.width(12.dp))

                // 商品信息
                Column(
                    modifier = Modifier.weight(1f)
                ) {
                    Text(
                        text = record.productName,
                        fontSize = 16.sp,
                        fontWeight = FontWeight.Bold,
                        fontFamily = CaiFont,
                        maxLines = 2
                    )

                    Spacer(modifier = Modifier.height(4.dp))

                    Text(
                        text = record.productCategory,
                        fontSize = 12.sp,
                        color = Color.Gray,
                        fontFamily = CaiFont
                    )

                    Spacer(modifier = Modifier.height(8.dp))

                    Row(
                        horizontalArrangement = Arrangement.SpaceBetween,
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Text(
                            text = "￥${record.productPrice}",
                            fontSize = 18.sp,
                            fontWeight = FontWeight.Bold,
                            color = Color(0xFFFF6F00),
                            fontFamily = CaiFont
                        )

                        // 分润状态
                        if (record.profitDistributed == 1) {
                            Surface(
                                shape = RoundedCornerShape(4.dp),
                                color = Color(0xFFFFB300)
                            ) {
                                Text(
                                    text = "已分润",
                                    fontSize = 10.sp,
                                    color = Color.White,
                                    fontFamily = CaiFont,
                                    modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.dp)
                                )
                            }
                        }
                    }
                }
            }

            Spacer(modifier = Modifier.height(12.dp))

            // 订单时间
            Text(
                text = "下单时间: ${formatOrderTime(record.orderTime)}",
                fontSize = 12.sp,
                color = Color.Gray,
                fontFamily = CaiFont
            )

            // 已支付订单显示百度网盘链接按钮（使用固定链接）
            if (record.payStatus == 1) {
                Spacer(modifier = Modifier.height(12.dp))

                OutlinedButton(
                    onClick = {
                        val baiduUrl = "https://pan.baidu.com/s/1vlVzDZ8DiSqyPnQzl-ECkw?pwd=92n5"
                        println("PurchaseRecordItem - 点击查看资源链接，订单号: ${record.orderNo}, 链接: $baiduUrl") // 日志输出
                        UrlLauncher.openUrl(baiduUrl)
                    },
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(8.dp),
                    colors = ButtonDefaults.outlinedButtonColors(
                        contentColor = Color(0xFF1E88E5)
                    )
                ) {
                    Icon(
                        imageVector = Icons.Default.Link,
                        contentDescription = "链接",
                        modifier = Modifier.size(20.dp)
                    )
                    Spacer(modifier = Modifier.width(8.dp))
                    Text(
                        text = "查看资源链接",
                        fontSize = 14.sp,
                        fontWeight = FontWeight.Medium,
                        fontFamily = CaiFont
                    )
                }
            }
        }
    }
}

/**
 * 格式化订单时间
 */
private fun formatOrderTime(time: String): String {
    return try {
        // 简单处理：去掉时区信息和毫秒
        time.replace(".000+00:00", "")
            .replace("T", " ")
    } catch (e: Exception) {
        println("PurchaseRecordScreen - 格式化时间失败: ${e.message}") // 日志输出
        time
    }
}